import './style.css';
import { Card } from 'primereact/card';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { LoadingComponent } from '../../controls';
import PlayerStatisticsService from '../../api/playerStatisticsService';
import {
  ClientVersionDistribution,
  emptyVersionDistribution,
  PlayerStatistics,
} from '../../types/playerStatistics';

const versionTotal = (d: ClientVersionDistribution) =>
  d.latestClientVersionCount + d.previousClientVersionCount + d.otherClientVersionsCount;

const pct = (part: number, total: number) => (total === 0 ? 0 : (100 * part) / total);

const segmentWidths = (d: ClientVersionDistribution, totalV: number) => {
  if (totalV === 0) {
    return { latest: 0, previous: 0, others: 0 };
  }
  const latest = (100 * d.latestClientVersionCount) / totalV;
  const previous = (100 * d.previousClientVersionCount) / totalV;
  const others = Math.max(0, 100 - latest - previous);
  return { latest, previous, others };
};

type VersionCardProps = {
  title: string;
  distribution: ClientVersionDistribution;
};

const VersionDistributionCard = ({ title, distribution: d }: VersionCardProps) => {
  const totalV = versionTotal(d);
  const pLatest = pct(d.latestClientVersionCount, totalV);
  const pPrevious = pct(d.previousClientVersionCount, totalV);
  const pOthers = pct(d.otherClientVersionsCount, totalV);
  const widths = segmentWidths(d, totalV);
  const latestLabel = d.latestClientVersion || '—';
  const previousLabel = d.previousClientVersion || '—';

  return (
    <Card className="player-statistics__card">
      <div className="text-lg font-semibold mb-2">{title}</div>
      <p className="text-color-secondary mt-0 mb-3">
        Latest and previous versions are ordered by semantic version comparison. All other versions and empty values are
        grouped as other.
      </p>
      {totalV === 0 ? (
        <p className="m-0">No version data.</p>
      ) : (
        <>
          <div className="player-statistics__bar" role="img" aria-label={`${title} version share`}>
            {d.latestClientVersionCount > 0 && (
              <div
                className="player-statistics__segment player-statistics__segment--latest"
                style={{ width: `${widths.latest}%` }}
                title={`Latest: ${latestLabel}`}
              />
            )}
            {d.previousClientVersionCount > 0 && (
              <div
                className="player-statistics__segment player-statistics__segment--previous"
                style={{ width: `${widths.previous}%` }}
                title={`Previous: ${previousLabel}`}
              />
            )}
            {d.otherClientVersionsCount > 0 && (
              <div
                className="player-statistics__segment player-statistics__segment--others"
                style={{ width: `${widths.others}%` }}
                title="Other versions"
              />
            )}
          </div>
          <div className="player-statistics__legend">
            <div className="player-statistics__legend-item">
              <span className="player-statistics__swatch player-statistics__swatch--latest" />
              <span>
                Latest ({latestLabel}): {d.latestClientVersionCount} ({pLatest.toFixed(1)}%)
              </span>
            </div>
            <div className="player-statistics__legend-item">
              <span className="player-statistics__swatch player-statistics__swatch--previous" />
              <span>
                Previous ({previousLabel}): {d.previousClientVersionCount} ({pPrevious.toFixed(1)}%)
              </span>
            </div>
            <div className="player-statistics__legend-item">
              <span className="player-statistics__swatch player-statistics__swatch--others" />
              <span>
                Other: {d.otherClientVersionsCount} ({pOthers.toFixed(1)}%)
              </span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

const emptyStats = (): PlayerStatistics => ({
  activeLast24HoursCount: 0,
  activeLast30DaysCount: 0,
  ios: emptyVersionDistribution(),
  android: emptyVersionDistribution(),
  other: emptyVersionDistribution(),
  clientVersions: [],
});

const PlayerStatisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PlayerStatistics | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PlayerStatisticsService.get();
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !stats) {
    return <LoadingComponent />;
  }

  const s = stats ?? emptyStats();

  return (
    <div className="page-player-statistics">
      <div className="flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
        <h1 className="text-xl m-0">Player statistics</h1>
        <Button type="button" icon="pi pi-refresh" label="Refresh" onClick={() => void load()} disabled={loading} />
      </div>

      <div className="player-statistics__cards player-statistics__cards--active">
        <Card className="player-statistics__card">
          <div className="text-lg font-semibold mb-2">Active players (last 24 hours)</div>
          <p className="text-3xl font-bold m-0">{s.activeLast24HoursCount}</p>
          <p className="text-color-secondary text-sm m-0 mt-2">Based on last activity (UTC).</p>
        </Card>
        <Card className="player-statistics__card">
          <div className="text-lg font-semibold mb-2">Active players (last 30 days)</div>
          <p className="text-3xl font-bold m-0">{s.activeLast30DaysCount}</p>
          <p className="text-color-secondary text-sm m-0 mt-2">Based on last activity (UTC).</p>
        </Card>
      </div>

      <div className="player-statistics__cards player-statistics__cards--versions">
        <VersionDistributionCard title="iOS client version distribution" distribution={s.ios} />
        <VersionDistributionCard title="Android client version distribution" distribution={s.android} />
        <VersionDistributionCard title="Other client version distribution" distribution={s.other} />
      </div>
    </div>
  );
};

export default PlayerStatisticsPage;
